import { useParams } from 'react-router-dom'
import { ModalEditComponentProps } from '../../../baseComponents/ModalsLayouts/types'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { FormRender } from '../../../forms/FormRenderer/types'
import { sortCustomerProjectStatuses } from '../SortCustomerProjectStatuses'
import { MediumModalCreateEdit } from '../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { GroupPayload } from '../../../utils/reducers/groups/groups'
import { EmployeesPayload } from '../../../utils/reducers/company/employees'
import { connect, ConnectedProps } from 'react-redux'
import { AllReduxPayloads } from '../../../utils/reducers'
import withLoading from '../../../utils/hoc/withLoading'
import { BeforeSubmitHandler } from '../../../forms/TwinForm/types'
import { TaskCFieldPayload } from '../../../utils/reducers/company/taskCFields'
import { CustomerProjectTypePayload, DictionaryCustomerProject } from '../../../utils/reducers/customers/customerProjectTypes'
import ReactDOM from 'react-dom'
import { ModalOpenedSetTy } from '../../../baseComponents/Modal/types'
import { useGetCustomerProjectTypeByIsMain } from '../../../utils/hooks/customer/useProjectsAndOpportunitiesDict'
import { addDictionaryExtraLine } from '../../../utils/globals/dictionary'
import { emptyExtraLineFirstSortFunction } from '../../../forms/CustomSelect/functions'
import { CustomerProjectAddModelTypeExtended } from '../../../app/erp/screens/Company/CustomerParent/CustomerProjects/types'
import { projectTypesInversed, ProjectTypesKeys } from '@teinor/erp/types/company/customer/customerProject'
import { TChecklistsPayload } from '../../../utils/reducers/customers/tChecklists'

type ModalCreateCustomerProjectProps = ModalEditComponentProps & ReduxCustomerEmployeeGroup & {}

const ModalCreateCustomerProject: React.FC<ModalCreateCustomerProjectProps> = ({ allRowData, setOpened, onSubmit, employees, groups, basicData, stopPropagation, customerProjectTypes, tChecklists }) => {
    const { t } = useTwinTranslation()
    const { id } = useParams()
    const CustomerId = basicData?.CustomerId || id
    const { myProjectStatuses, setMyProjectTypeId, handleBeforeSubmit, myProjectTypeId } = useModalCreateCustomerProjectLogic({ id: allRowData?.CustomerProjectTypeId, customerProjectTypes })
    const checkListExtraLineDict = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(tChecklists || {}))), [tChecklists])
    const fields: FormRender<CustomerProjectAddModelTypeExtended> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'name',
                    component: 'InputWithLabelMargin',
                    label: t('name', 'Nombre'),
                    required: true
                },
                {
                    name: 'description',
                    component: 'TextArea',
                    label: t('description', 'Descripción'),
                },
                {
                    name: 'CustomerProjectTypeId',
                    component: 'CustomSelectWithSearchBar',
                    required: true,
                    value: myProjectTypeId,
                    label: t('projectType', 'Tipo de proyecto'),
                    items: customerProjectTypes || {},
                    onChange: (value) => setMyProjectTypeId(value)
                },
                {
                    name: 'CurrentCProjectStatusId',
                    component: 'CustomSelectColor',
                    label: t('projectStatus', 'Tipo de estado'),
                    items: myProjectStatuses || {},
                    value: customerProjectTypes?.[parseInt(myProjectTypeId || '0')]?.CustomerProjectStatusesByType?.project?.[0]?.id || '',
                    extraDropDownParams: {
                        sortFunction: sortCustomerProjectStatuses
                    }
                },
                {
                    name: 'TChecklistId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('defaultList', 'Lista por defecto'),
                    items: checkListExtraLineDict || {},
                    value: customerProjectTypes?.[myProjectTypeId as any]?.CPChecklistId,
                    sortFunction: emptyExtraLineFirstSortFunction
                },
                {
                    name: 'limit_date',
                    label: t('limitDate', 'Fecha límite'),
                    component: 'InputCalendarStateFull',
                    onlyValids: true,
                },
                {
                    name: 'CustomerId',
                    component: 'InputHidden',
                    value: CustomerId
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: allRowData?.id
                },
                {
                    name: 'projectType',
                    component: 'InputHidden',
                    value: 1,
                }
            ]
        },
        {
            cols: 1,
            title: t('employees', 'Empleados'),
            elements: [
                {
                    name: 'Employees',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('notSelected', 'Sin seleccionar'), subtitleSelectedOptions: t('selected', 'Seleccionados') },
                    items: employees || {},
                }
            ]
        },
        {
            cols: 1,
            title: t('groups', 'Grupos'),
            elements: [
                {
                    name: 'Groups',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('notSelected', 'Sin seleccionar'), subtitleSelectedOptions: t('selected', 'Seleccionados') },
                    items: groups || {},
                }
            ]
        }
    ]
    return (
        <MediumModalCreateEdit  url='/api/app/customer/customerProject/createCustomerProject' fields={fields} setOpened={setOpened} stopPropagation={stopPropagation} onSubmit={onSubmit} translations={{
            title: t('createProject', 'Crear proyecto'),
            button: t('create', 'Crear')
        }} haveButtonPermissions={true} beforeSubmit={handleBeforeSubmit}>
        </MediumModalCreateEdit>
    )
}

interface ModalCreateCustomerProjectLogicProps {
    id: number
    customerProjectTypes: DictionaryCustomerProject | null
}


const useModalCreateCustomerProjectLogic = ({ id, customerProjectTypes }: ModalCreateCustomerProjectLogicProps) => {
    const idProjectType = id !== undefined ? String(id) : ''
    const { getCustomerProjectTypeByIsMain } = useGetCustomerProjectTypeByIsMain()
    const customerProjectTypeByIsMain = useMemo(() => getCustomerProjectTypeByIsMain(customerProjectTypes, projectTypesInversed['project'] as ProjectTypesKeys), [customerProjectTypes, getCustomerProjectTypeByIsMain])
    const [myProjectTypeId, setMyProjectTypeId] = useState<string>(idProjectType || customerProjectTypeByIsMain?.id)
    const myProjectStatuses = useMemo(() => {
        return myProjectTypeId ? customerProjectTypes?.[parseInt(myProjectTypeId)].customerProjectStatusDict.project : {}
    }, [myProjectTypeId, customerProjectTypes])

    const handleBeforeSubmit: BeforeSubmitHandler = useCallback((values: any) => {
        if (values.CurrentCProjectStatusId && !myProjectStatuses?.[values.CurrentCProjectStatusId]) {
            values.CurrentCProjectStatusId = null
        }
        return true
    }, [myProjectStatuses])

    return { customerProjectTypes, myProjectStatuses, setMyProjectTypeId, handleBeforeSubmit, myProjectTypeId }
}

interface ModalCreateCustomerProjectOutsideProps extends ModalCreateCustomerProjectOutsideLogicProps { }

export const ModalCreateCustomerProjectOutside: React.FC<ModalCreateCustomerProjectOutsideProps> = ({ ...rest }) => {
    const { mySetOpened } = useModalCreateCustomerProjectOutsideLogic({...rest})
    return (
        ReactDOM.createPortal(
            <ModalCreateCustomerProjectConnect {...rest as any} setOpened={mySetOpened} stopPropagation={true} />,
            document.body
        )
    )
}
interface ModalCreateCustomerProjectOutsideLogicProps extends ModalEditComponentProps {
    setLockOnClickOut?: (lock: boolean) => void
}   
const useModalCreateCustomerProjectOutsideLogic = ({ setOpened, setLockOnClickOut }: ModalCreateCustomerProjectOutsideLogicProps) => {
    const mySetOpened: ModalOpenedSetTy = useCallback((value) => {
        if (value === null) {
            setLockOnClickOut?.(false)
        }
        setOpened(value)
    }, [setLockOnClickOut, setOpened])

    useEffect(() => {
        setLockOnClickOut?.(true)
    }, [setLockOnClickOut])
    return { mySetOpened }
}

const customerEmployeeGroupDispatch = {
    setGroups: (payload: GroupPayload) => ({ type: 'CHANGE_GROUP', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
    setTaskCFields: (payload: TaskCFieldPayload) => ({ type: 'CHANGE_TASKCFIELD', payload }),
    setCustomerProjectTypes: (payload: CustomerProjectTypePayload) => ({ type: 'CHANGE_CUSTOMER_PROJECT_TYPES', payload }),
    setTChecklists: (payload: TChecklistsPayload) => ({ type: 'CHANGE_T_CHECKLISTS', payload })
}

export type ReduxCustomerEmployeeGroup = ConnectedProps<typeof customerEmployeeGroupConnect>
const mapCustomerEmployeeGroupConnector = (state: AllReduxPayloads) => ({ groups: state.groups, employees: state.employees, taskCFields: state.taskCFields, customerProjectTypes: state.customerProjectTypes, tChecklists: state.tChecklists })
const customerEmployeeGroupConnect = connect(mapCustomerEmployeeGroupConnector, customerEmployeeGroupDispatch)

const customerEmployeeGroupConnectLoading = withLoading(ModalCreateCustomerProject, [{ fetchUrl: '/api/app/group/getAllGroupsListing', propName: 'groups', setFunctionName: 'setGroups' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }, { fetchUrl: '/api/app/task/customField/getAllTaskCFieldsComplete', propName: 'taskCFields', setFunctionName: 'setTaskCFields' }, { fetchUrl: '/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypesFull', propName: 'customerProjectTypes', setFunctionName: 'setCustomerProjectTypes' }, { fetchUrl: '/api/app/config/templateChecklist/getAllTChecklists', propName: 'tChecklists', setFunctionName: 'setTChecklists' }])

const ModalCreateCustomerProjectConnect = customerEmployeeGroupConnect(customerEmployeeGroupConnectLoading)


export default ModalCreateCustomerProjectConnect